import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { internDetailsApi } from '../../../middleware/api/InternDetails/internDetailsApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  InternDetailsPagination,
  IInternDetailsProps,
  InternAddTopicProps,
  InternDetailsAddEdit,
  InternDetailsSliceState,
  InternEmployeeName,
  InternEmployeeResponse,
  InternTopicResponse,
  InternFilterOptionsType,
  InternCollegeResponse,
  internProps,
  InterHistoryProps,
} from '../../../types/EmployeeDirectory/InternDetails/internDetailsTypes'
import { RootState } from '../../../stateStore'

// Type for API response that can be either array or object with list property
type InternHistoryApiResponse =
  | InterHistoryProps[]
  | {
      list: InterHistoryProps[]
      size?: number
    }

//THUNKS
const getInternDetails = createAsyncThunk(
  'internDetails/getInternDetails',
  async (props: IInternDetailsProps, thunkApi) => {
    try {
      return await internDetailsApi.getInternDetailsList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const addInternDetails = createAsyncThunk(
  'internDetails/addInternDetails',
  async (props: InternDetailsAddEdit, thunkApi) => {
    try {
      return await internDetailsApi.addIntern(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const editInternDetails = createAsyncThunk(
  'internDetails/editInternDetails',
  async (props: InternDetailsAddEdit, thunkApi) => {
    try {
      return await internDetailsApi.editIntern(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const addInternTopic = createAsyncThunk(
  'internDetails/addInternTopic',
  async (props: InternAddTopicProps, thunkApi) => {
    try {
      return await internDetailsApi.addTopic(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllTopics = createAsyncThunk(
  'internDetails/getAllTopics',
  async (__, thunkApi) => {
    try {
      return await internDetailsApi.getAllTopics()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const deleteTopic = createAsyncThunk(
  'internDetails/deleteTopic',
  async (props: number, thunkApi) => {
    try {
      return await internDetailsApi.deleteTopic(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addInternEmployee = createAsyncThunk(
  'internDetails/addInternEmployee',
  async (props: InternEmployeeName, thunkApi) => {
    try {
      return await internDetailsApi.addEmployeeName(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getAllEmployees = createAsyncThunk(
  'internDetails/getAllEmployees',
  async (__, thunkApi) => {
    try {
      return await internDetailsApi.getAllEmployeeNames()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteEmployeeName = createAsyncThunk(
  'internDetails/deleteEmployeeName',
  async (props: number, thunkApi) => {
    try {
      return await internDetailsApi.deleteEmployeeName(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllCollegeNames = createAsyncThunk(
  'internDetails/getAllCollegeNames',
  async (__, thunkApi) => {
    try {
      return await internDetailsApi.getAllCollegeNames()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getInternProfileHistory = createAsyncThunk(
  'internDetails/getInternProfileHistory',
  async (props: internProps, thunkApi) => {
    try {
      return await internDetailsApi.internTimelineApi(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

//END OF THUNKS

const initialInternDetailsFormState: InternDetailsSliceState = {
  internDetailsList: [] as InternDetailsAddEdit[],
  selectedInternDetails: {} as InternDetailsAddEdit,

  isLoading: ApiLoadingState.idle,
  isLoadingAdd: ApiLoadingState.idle,
  isLoadingEdit: ApiLoadingState.idle,
  isLoadingAddTopic: ApiLoadingState.idle,
  isLoadingGetTopic: ApiLoadingState.idle,
  isLoadingEmployee: ApiLoadingState.idle,
  isLoadingHistory: ApiLoadingState.idle,

  internDetailsObject: {} as IInternDetailsProps,
  listSize: 0,
  internEmployeeList: [] as InternEmployeeResponse[],
  internCollegeList: [] as InternCollegeResponse[],
  internTopicList: [] as InternTopicResponse[],
  internHistoryList: [] as InterHistoryProps[],

  pagination: {} as InternDetailsPagination,
  filters: {
    searchStr: '',
    topic: '',
    college: '',
    technology: '',
    selectionDate: '',
    from: '',
    to: '',
  } as InternFilterOptionsType,

  isFromCrud: false,
}

const internDetailsSlice = createSlice({
  name: 'internDetails',
  initialState: initialInternDetailsFormState,
  reducers: {
    setFilterValues: (state, action) => {
      state.filters = action.payload
    },
    clearFilterValues: (state) => {
      state.filters = {
        searchStr: '',
        topic: '',
        college: '',
        technology: '',
        selectionDate: '',
        from: '',
        to: '',
      }
    },
    setSelectedInternDetails: (state, action) => {
      state.selectedInternDetails = action.payload
    },
    setIsFromCrud: (state, action) => {
      state.isFromCrud = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInternDetails.pending, (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addCase(getInternDetails.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.internDetailsList = action.payload.internslist
      state.listSize = action.payload.size
    })
    builder.addCase(getInternDetails.rejected, (state) => {
      state.isLoading = ApiLoadingState.failed
    })

    builder.addCase(addInternDetails.pending, (state) => {
      state.isLoadingAdd = ApiLoadingState.loading
    })
    builder.addCase(addInternDetails.fulfilled, (state) => {
      state.isLoadingAdd = ApiLoadingState.succeeded
    })
    builder.addCase(addInternDetails.rejected, (state) => {
      state.isLoadingAdd = ApiLoadingState.failed
    })

    builder.addCase(editInternDetails.pending, (state) => {
      state.isLoadingEdit = ApiLoadingState.loading
    })
    builder.addCase(editInternDetails.fulfilled, (state) => {
      state.isLoadingEdit = ApiLoadingState.succeeded
    })
    builder.addCase(editInternDetails.rejected, (state) => {
      state.isLoadingEdit = ApiLoadingState.failed
    })

    builder.addCase(getAllTopics.pending, (state) => {
      state.isLoadingGetTopic = ApiLoadingState.loading
    })
    builder.addCase(getAllTopics.fulfilled, (state, action) => {
      state.isLoadingGetTopic = ApiLoadingState.succeeded
      state.internTopicList = action.payload as InternTopicResponse[]
    })
    builder.addCase(getAllTopics.rejected, (state) => {
      state.isLoadingGetTopic = ApiLoadingState.failed
    })

    builder.addCase(addInternTopic.pending, (state) => {
      state.isLoadingAddTopic = ApiLoadingState.loading
    })
    builder.addCase(addInternTopic.fulfilled, (state) => {
      state.isLoadingAddTopic = ApiLoadingState.succeeded
    })
    builder.addCase(addInternTopic.rejected, (state) => {
      state.isLoadingAddTopic = ApiLoadingState.failed
    })

    builder.addCase(getAllEmployees.fulfilled, (state, action) => {
      state.internEmployeeList = action.payload
      state.isLoadingEmployee = ApiLoadingState.succeeded
    })

    builder.addCase(getAllEmployees.pending, (state) => {
      state.isLoadingEmployee = ApiLoadingState.loading
    })

    builder.addCase(getAllEmployees.rejected, (state) => {
      state.isLoadingEmployee = ApiLoadingState.failed
    })

    builder.addCase(addInternEmployee.pending, (state) => {
      state.isLoadingEmployee = ApiLoadingState.loading
    })

    builder.addCase(addInternEmployee.rejected, (state) => {
      state.isLoadingEmployee = ApiLoadingState.failed
    })

    builder.addCase(getAllCollegeNames.fulfilled, (state, action) => {
      state.internCollegeList = action.payload
    })

    builder.addCase(getInternProfileHistory.pending, (state) => {
      state.isLoadingHistory = ApiLoadingState.loading
    })
    builder.addCase(getInternProfileHistory.fulfilled, (state, action) => {
      state.isLoadingHistory = ApiLoadingState.succeeded
      const payload = action.payload as InternHistoryApiResponse
      state.internHistoryList = Array.isArray(payload)
        ? payload
        : payload?.list || []
    })

    builder.addCase(getInternProfileHistory.rejected, (state) => {
      state.isLoadingHistory = ApiLoadingState.failed
    })

    builder.addCase(addInternEmployee.fulfilled, (state) => {
      state.isLoadingEmployee = ApiLoadingState.succeeded
    })
  },
})

//Getters
const listSize = (state: RootState): number => state.internDetails.listSize

const getIsLoading = (state: RootState): LoadingState =>
  state.internDetails.isLoading

const getIsLoadingAdd = (state: RootState): LoadingState =>
  state.internDetails.isLoadingAdd

const getIsLoadingEdit = (state: RootState): LoadingState =>
  state.internDetails.isLoadingEdit

const getIsLoadingAddTopic = (state: RootState): LoadingState =>
  state.internDetails.isLoadingAddTopic

const getIsLoadingGetTopics = (state: RootState): LoadingState =>
  state.internDetails.isLoadingGetTopic

const getIsLoadingEmployee = (state: RootState): LoadingState =>
  state.internDetails.isLoadingEmployee

const getIsLoadingHistory = (state: RootState): LoadingState =>
  state.internDetails.isLoadingHistory

const getInternHistory = (state: RootState): InterHistoryProps[] =>
  state.internDetails.internHistoryList

const getInternDetailsList = (state: RootState): InternDetailsAddEdit[] =>
  state.internDetails.internDetailsList

const getInternCollegeList = (state: RootState): InternCollegeResponse[] =>
  state.internDetails.internCollegeList

const getInternEmployeeList = (state: RootState): InternEmployeeResponse[] =>
  state.internDetails.internEmployeeList

const getTopics = (state: RootState): InternTopicResponse[] =>
  state.internDetails.internTopicList

const getSelectedIntern = (state: RootState): InternDetailsAddEdit =>
  state.internDetails.selectedInternDetails

const getFilterValues = (state: RootState): InternFilterOptionsType =>
  state.internDetails.filters

const getIsFromCrud = (state: RootState): boolean =>
  state.internDetails.isFromCrud

const internDetailsSelectors = {
  listSize,
  getIsLoading,
  getInternDetailsList,
  getIsLoadingAdd,
  getIsLoadingEdit,
  getSelectedIntern,
  getIsLoadingAddTopic,
  getIsLoadingGetTopics,
  getTopics,
  getInternEmployeeList,
  getInternCollegeList,
  getInternHistory,
  getIsLoadingEmployee,
  getFilterValues,
  getIsFromCrud,
  getIsLoadingHistory,
}

export const internDetailsThunk = {
  addInternDetails,
  getInternDetails,
  editInternDetails,
  addInternTopic,
  getAllTopics,
  deleteTopic,
  addInternEmployee,
  getAllEmployees,
  deleteEmployeeName,
  getAllCollegeNames,
  getInternProfileHistory,
}

export const internDetailsService = {
  ...internDetailsThunk,
  actions: internDetailsSlice.actions,
  selectors: internDetailsSelectors,
}

export default internDetailsSlice.reducer
